import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { environment } from '../../../environments/environment';

/** The two funnel events this app emits into GetHub's native Website Analytics. */
export type GcTrackerEventName = 'renters_landing' | 'policy_purchase';
type GcTrackerValue = string | number | boolean | null;
type GcTrackerProperties = Record<string, GcTrackerValue>;
type GcTrackerQueueItem = readonly [GcTrackerEventName, GcTrackerProperties?];

interface GcTrackerWindow {
  gcTracker?: { track: (name: string, properties?: GcTrackerProperties) => void };
  gcTrackerQueue?: GcTrackerQueueItem[];
}

/**
 * Funnel hop 3 (renters.getcoveredinsurance.com) into GetHub's native Website
 * Analytics. Loads the public loader and emits the two funnel events —
 * renters_landing and policy_purchase — that tie a getcovered.io click through
 * to a completed sale.
 *
 * Everything here is browser-only and best-effort: it must never throw into the
 * purchase flow, block rendering, or run under SSR. Per the V1 funnel contract
 * this site emits FUNNEL EVENTS ONLY; its GetHub site config keeps
 * replay/heatmap/page-view recording off, so the payment flow's PII is never
 * captured.
 */
@Injectable({ providedIn: 'root' })
export class GethubTrackerService {
  private readonly isBrowser: boolean;

  constructor(
    @Inject(PLATFORM_ID) platformId: object,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  /**
   * Inject the public loader once. No-op on the server, without a configured
   * site key, or when the tag is already present.
   */
  injectLoader(): void {
    try {
      if (!this.isBrowser) return;
      const cfg = environment.analytics;
      if (!cfg || !cfg.trackerUrl || !cfg.siteKey) return;
      if (this.document.getElementById('gethub-website-tracker')) return;
      const script = this.document.createElement('script');
      script.id = 'gethub-website-tracker';
      script.src = cfg.trackerUrl;
      script.async = true;
      script.setAttribute('data-site', cfg.siteKey);
      // Manual page mode: this site never emits page views, only funnel events.
      script.setAttribute('data-initial-page', 'manual');
      (this.document.head || this.document.body).appendChild(script);
    } catch {
      // Analytics is best-effort and must never affect the site.
    }
  }

  /**
   * Fire renters_landing at most once per inbound click id per browser session.
   * The loader promotes gc_click_id into the envelope and joins this landing to
   * the originating getcovered.io click.
   */
  trackLandingOnce(clickId: string | null | undefined): void {
    try {
      if (!this.isBrowser || !clickId) return;
      const key = `gc_renters_landing:${clickId}`;
      if (sessionStorage.getItem(key) === '1') return;
      this.track('renters_landing', { gc_click_id: clickId });
      // Set after queueing so a refresh cannot re-fire even while the loader
      // is still arriving.
      sessionStorage.setItem(key, '1');
    } catch {
      // best-effort only
    }
  }

  /**
   * Fire policy_purchase exactly once per transaction (quote) id. Requires
   * BOTH ids: the funnel click id (the collector rejects a purchase without
   * one, and only funnel conversions belong in this event) and the transaction
   * id (it is the dedupe key — without it a retried callback would overcount a
   * conversion, and for a conversion event undercounting beats overcounting;
   * every purchase call site has a quote id, so a missing one is itself a
   * bug). Dedupes reloads/retries within the session so a completed sale is
   * counted exactly once. Carries only the join key — never an amount or any
   * PII.
   */
  trackPurchaseOnce(
    transactionId: string | null | undefined,
    clickId: string | null | undefined,
  ): void {
    try {
      if (!this.isBrowser || !clickId || !transactionId) return;
      const dedupeKey = `gc_policy_purchase:${transactionId}`;
      if (sessionStorage.getItem(dedupeKey) === '1') return;
      this.track('policy_purchase', { gc_click_id: clickId });
      sessionStorage.setItem(dedupeKey, '1');
    } catch {
      // best-effort — a missed analytics call must never affect a completed sale
    }
  }

  /**
   * Queue-safe emit: events sent before the loader arrives wait in
   * window.gcTrackerQueue and are drained by the loader when it installs
   * window.gcTracker.
   */
  private track(name: GcTrackerEventName, properties?: GcTrackerProperties): void {
    if (!this.isBrowser) return;
    const win = window as unknown as GcTrackerWindow;
    if (typeof win.gcTracker?.track === 'function') {
      win.gcTracker.track(name, properties);
      return;
    }
    if (!win.gcTrackerQueue) win.gcTrackerQueue = [];
    win.gcTrackerQueue.push([name, properties]);
  }
}
